import React from 'react';
import { Clock, MessageCircleX, Wrench } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';

const problems = [
  {
    icon: Clock,
    title: "Projects Take Forever to Launch",
    description: "Your idea sits in development hell for months while competitors ship faster.",
    impact: "Lost market opportunity"
  },
  {
    icon: MessageCircleX, 
    title: "No Clear Communication",
    description: "You don't know what's happening until something goes wrong.",
    impact: "Stress and uncertainty"
  },
  {
    icon: Wrench,
    title: "Technical Debt Slows Growth", 
    description: "Your app becomes harder to update and costs more to maintain.",
    impact: "Higher long-term costs"
  }
];

export const ProblemsSection = () => {
  const { elementRef, getRevealStyle } = useScrollReveal();

  return (
    <section className="section-spacing bg-background">
      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-content mx-auto">
          <div className="text-center mb-20">
            <div className="micro-label mb-4 text-primary">
              THE REAL PROBLEM
            </div>
            <h2 className="heading-section text-foreground mb-8">
              Every Business Faces These <span className="accent-keyword">Challenges</span>
            </h2>
            <p className="text-enterprise text-body max-w-measure mx-auto">
              These issues compound over time, making them more expensive to fix later.
            </p>
          </div>

          <div ref={elementRef} className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {problems.map((problem, index) => {
              const Icon = problem.icon;
              
              return (
                <div 
                  key={problem.title}
                  className="group relative"
                  style={getRevealStyle(index + 1)}
                >
                  <div className="bg-[rgba(255,255,255,0.04)] backdrop-blur-md border border-[rgba(255,255,255,0.14)] rounded-3xl p-12 text-center h-full transition-all duration-400 hover:transform hover:-translate-y-2 hover:border-[rgba(59,130,246,0.4)] hover:shadow-lg hover:bg-[rgba(255,255,255,0.07)]">
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-primary opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
                    
                    <div className="w-14 h-14 mx-auto mb-6 bg-[rgba(59,130,246,0.15)] rounded-2xl flex items-center justify-center group-hover:bg-[rgba(59,130,246,0.25)] transition-colors">
                      <Icon className="w-7 h-7 text-primary" strokeWidth={1.5} />
                    </div>
                    
                    <h3 className="text-2xl font-bold text-white mb-4">
                      {problem.title}
                    </h3>
                    
                    <p className="text-lg leading-relaxed mb-6" style={{ color: 'rgba(255, 255, 255, 0.90)' }}>
                      {problem.description}
                    </p>
                    
                    <div className="text-sm font-semibold text-primary">
                      {problem.impact}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};